// Wave 3 — Public Services page (dynamic; count reflects live seed).
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import PublicShell, { CapabilityTile } from "../../components/public/PublicShell";
import { api } from "../../lib/api";

// Small helper to render the service count in words for the intro copy.
const COUNT_IN_WORDS = {
  21: "Twenty-one", 22: "Twenty-two", 23: "Twenty-three", 24: "Twenty-four",
  25: "Twenty-five", 26: "Twenty-six", 27: "Twenty-seven", 28: "Twenty-eight",
  29: "Twenty-nine", 30: "Thirty", 31: "Thirty-one", 32: "Thirty-two",
};

// R1d.7 — Category order + display labels for the filter chip strip.
const CATEGORY_ORDER = [
  { id: "all", label: "All" },
  { id: "residential", label: "Residential" },
  { id: "commercial", label: "Commercial" },
  { id: "restoration", label: "Restoration" },
  { id: "trades", label: "Trades" },
  { id: "advisory", label: "Advisory" },
  { id: "outdoor", label: "Outdoor" },
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [params, setParams] = useSearchParams();
  const { categorySlug } = useParams();
  // R1d.8 — path param takes precedence over query param so canonical
  // /services/category/{slug} URLs always reflect their filter.
  const activeCategory = categorySlug || params.get("category") || "all";

  useEffect(() => {
    api.get("/public/website/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  // R1d.8 — Set canonical link tag on path-form URLs to consolidate SEO
  // signal (both /services?category=X and /services/category/X exist).
  useEffect(() => {
    let link = document.querySelector("link[rel='canonical']");
    const shouldSet = activeCategory && activeCategory !== "all";
    if (shouldSet) {
      const href = `${window.location.origin}/services/category/${activeCategory}`;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
    } else if (link && link.getAttribute("data-managed-by") === "services-page") {
      link.parentNode.removeChild(link);
    }
    if (link) link.setAttribute("data-managed-by", "services-page");
    return () => {
      const l = document.querySelector("link[rel='canonical'][data-managed-by='services-page']");
      if (l) l.parentNode.removeChild(l);
    };
  }, [activeCategory]);

  // R1d.8 — 404 for unknown category slugs on the path form.
  // NOTE: This early-return must live AFTER all hooks to preserve
  // hook call order (react-hooks/rules-of-hooks).
  const knownCategoryIds = CATEGORY_ORDER.map((c) => c.id);
  const isUnknownCategory = Boolean(categorySlug) && !knownCategoryIds.includes(categorySlug);

  const countWord = COUNT_IN_WORDS[services.length] || String(services.length);

  // Compute per-category counts once so chips update if seed changes.
  const categoryCounts = useMemo(() => {
    const counts = { all: services.length };
    for (const c of CATEGORY_ORDER) {
      if (c.id === "all") continue;
      counts[c.id] = services.filter((s) => (s.categories || []).includes(c.id)).length;
    }
    return counts;
  }, [services]);

  const filtered = useMemo(() => {
    if (activeCategory === "all") return services;
    return services.filter((s) => (s.categories || []).includes(activeCategory));
  }, [services, activeCategory]);

  if (isUnknownCategory) {
    return <Navigate to={`/services/category-not-found/${encodeURIComponent(categorySlug)}`} replace />;
  }

  function selectCategory(id) {
    // R1d.8 — keep both URL forms in sync. When arrived via path form,
    // use path form; otherwise use query form. Preserves back/forward.
    if (categorySlug !== undefined) {
      const target = id === "all" ? "/services" : `/services/category/${id}`;
      window.history.pushState({}, "", target);
      // Trigger a soft route change by dispatching a popstate — React
      // Router's useParams will re-read.
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }
    if (id === "all") {
      params.delete("category");
    } else {
      params.set("category", id);
    }
    setParams(params, { replace: false });
  }

  const hero = (
    <section className="erf-public-hero" data-testid="services-hero" style={{ padding: "72px 0 56px" }}>
      <div className="erf-public-container">
        <div className="erf-public-hero-eyebrow">Services</div>
        <h1 style={{ maxWidth: 800 }}>Every service we deliver, in one place.</h1>
        <p className="lead" data-testid="services-lead">
          {countWord} distinct service offerings across residential, commercial, restoration, and consulting. Every image on this page is an example of the work we perform — not a claim about any specific completed project. Real completed-work is on our <Link to="/portfolio" style={{ color: "var(--exec-accent)", textDecoration: "underline" }}>Portfolio</Link>.
        </p>
      </div>
    </section>
  );

  return (
    <PublicShell hero={hero}>
      <section className="erf-public-section" data-testid="services-grid-section">
        <div className="erf-public-container">
          {/* R1d.7 — Filter chip strip (sticky on scroll for large screens). */}
          <div
            data-testid="services-chip-strip"
            role="tablist"
            aria-label="Service category filters"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              padding: "0 0 20px",
              marginBottom: 24,
              borderBottom: "1px solid var(--exec-border)",
              position: "sticky",
              top: 72,
              zIndex: 30,
              background: "var(--exec-bg)",
            }}
          >
            {CATEGORY_ORDER.map((cat) => {
              const count = categoryCounts[cat.id] || 0;
              const isActive = cat.id === activeCategory;
              return (
                <button
                  key={cat.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => selectCategory(cat.id)}
                  data-testid={`services-chip-${cat.id}`}
                  data-active={isActive ? "true" : "false"}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    border: isActive ? "1px solid var(--exec-accent)" : "1px solid var(--exec-border)",
                    background: isActive ? "var(--exec-accent)" : "transparent",
                    color: isActive ? "#0F0F10" : "var(--exec-body)",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    letterSpacing: "0.02em",
                    cursor: "pointer",
                    transition: "background 0.15s, color 0.15s, border-color 0.15s",
                  }}
                >
                  {cat.label} <span style={{ opacity: 0.7, marginLeft: 4, fontFamily: "ui-monospace, monospace", fontSize: 11 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <div
              className="erf-public-empty-state"
              data-testid="services-empty-state"
              style={{ padding: "40px 0", textAlign: "center", color: "var(--exec-body)", fontStyle: "italic" }}
            >
              No services in this category yet.
            </div>
          ) : (
            <div className="erf-public-services-grid" data-testid="services-grid">
              {filtered.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="erf-public-service-card" data-testid={`service-card-${s.slug}`}>
                  <CapabilityTile service={s} alt={`${s.name} — ERF ENTERPRISES LLC`} />
                  <div className="erf-public-service-card-body">
                    <h3>{s.name}</h3>
                    <p>{s.short_description}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
